import { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import { Box, Button, Grid, ListSubheader, Stack, Typography, LinearProgress } from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import Image from '../../Image';
// hooks
import useLocales from '../../../hooks/useLocales';

import { useDispatch, useSelector } from '../../../redux/store';
import { setActiveTab } from '../../../redux/slices/game';
import { openModal } from '../../../redux/slices/profileSlice';

import useResponsive from '../../../hooks/useResponsive';
import Iconify from '../../Iconify';
import palette from '../../../theme/palette';
import useAuth from '../../../hooks/useAuth';
import { fCurrency } from '../../../utils/formatNumber';
import LanguagePopover from '../../../layouts/dashboard/header/LanguagePopover';
import Asia889 from '../../../assets/padu99.png';
import PinataWins from '../../../assets/pinatawins.jpg';

// ----------------------------------------------------------------------

export const ListSubheaderStyle = styled((props) => <ListSubheader disableSticky disableGutters {...props} />)(
  ({ theme }) => ({
    ...theme.typography.overline,
    paddingTop: theme.spacing(3),
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    color: theme.palette.text.primary,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shorter,
    }),
  })
);

// ----------------------------------------------------------------------

NavSectionVertical.propTypes = {
  isCollapse: PropTypes.bool,
  navConfig: PropTypes.array,
  onCloseSidebar: PropTypes.func,
};

export const MEMBER_VIP_IMGS = {
  normal: require('../../../assets/side-nav/vip_1.png'),
  bronze: require('../../../assets/side-nav/vip_1.png'),
  silver: require('../../../assets/side-nav/vip_2.png'),
  gold: require('../../../assets/side-nav/vip_3.png'),
  platinum: require('../../../assets/side-nav/vip_4.png'),
  diamond: require('../../../assets/side-nav/vip_5.png'),
  royal: require('../../../assets/side-nav/vip_6.png'),
};

export default function NavSectionVertical({ navConfig, isCollapse = false, onCloseSidebar = () => {}, ...other }) {
  const theme = useTheme();

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { translate, currentLang } = useLocales();

  const [bannerIdx1, setBannerIdx1] = useState(null);

  const { gameCategsAndCompanies, withdrawRequirement, customPages, activeTab, memberInfo, memberBalance, banner } =
    useSelector((x) => ({
      ...x.providers,
      ...x.lookup,
      ...x.game,
    }));

  const isDesktop = useResponsive('up', 'lg');
  const { isAuthenticated, user } = useAuth();

  const affiliatePage = useMemo(() => {
    if (customPages?.length) {
      const affiliateById = customPages.find((x) => x.pageKey === 'affiliates');

      if (affiliateById) {
        return affiliateById;
      }
    }
    return null;
  }, [customPages]);

  const menuItems = [
    { icon: 'mingcute:download-fill', label: 'download', path: '/game-download', key: 'game-download' },
    { icon: 'mdi:shield-account', label: 'responsible_gaming', path: '/responsible-gaming', key: 'responsible-gaming' },
    { icon: 'mdi:account-group', label: 'about_us_profile', path: '/about-us', key: 'about-us' },
    { icon: 'mdi:file-document', label: 'terms_and_condition', path: '/tnc', key: 'terms' },
    { icon: 'mdi:shield-lock', label: 'privacy_policy', path: '/privacy', key: 'privacy' },
  ];

  // const location = useLocation();

  // useEffect(() => {
  //   if (banner) {
  //     const _bannerIdx1 = banner?.filter((f) => f?.language?.toLowerCase() === currentLang.value);
  //     if (_bannerIdx1[0]) {
  //       setBannerIdx1(_bannerIdx1[0]?.image);
  //     }
  //   }
  // }, [currentLang, banner]);

  const MenuItem = ({ event, icon, label }) => {
    const isActive = activeTab === label;

    return (
      <Stack
        direction={isCollapse ? 'column' : 'row'}
        onClick={event}
        alignItems="center"
        justifyContent={isCollapse ? 'center' : 'flex-start'}
        spacing={isCollapse ? 0 : 1.5}
        sx={{
          cursor: 'pointer',
          borderRadius: 1.5,
          width: '100%',
          minWidth: '100%',
          height: isCollapse ? 48 : 46,
          px: isCollapse ? 0 : 2,
          background: isActive ? theme.palette.gradients.theme : '#F6F7F9',
          '& .nav-item-icon': {
            filter: isActive ? 'brightness(0) invert(1)' : 'none',
          },
          '& .nav-item-label': {
            color: isActive ? '#fff' : '#8991A7',
          },
          ':hover': {
            background: theme.palette.gradients.theme,
            '& .nav-item-icon': {
              filter: 'brightness(0) invert(1)',
            },
            '& .nav-item-label': {
              color: '#fff',
            },
          },
        }}
      >
        <Image
          className="nav-item-icon"
          src={MENU_ICONS[icon]}
          sx={{
            width: 22,
            height: 22,
          }}
        />

        {!isCollapse && (
          <Typography
            className="nav-item-label"
            sx={{
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {translate(label)}
          </Typography>
        )}
      </Stack>
    );
  };

  let imageSrc = null;

  if (banner?.length > 0) {
    imageSrc = isCollapse ? Asia889 : PinataWins;
  }

  return (
    <Box {...other}>
      <Stack
        direction="column"
        spacing={2}
        sx={{
          borderRadius: 1,
          mb: 1,
          paddingX: 1,
          width: '100%',
          alignItems: 'center',
        }}
      >
        <Stack
          direction={isCollapse ? 'column' : 'row'}
          spacing={isCollapse ? 0 : 2}
          alignItems="center"
          justifyContent={isCollapse ? 'center' : 'flex-start'}
          sx={{ cursor: 'pointer', py: 1 }}
        >
          <Image
            src={require('../../../assets/side-nav/profile.png')}
            sx={{
              width: 50,
              height: 50,
            }}
          />

          {/* Hide all text/content when collapsed */}
          {!isCollapse && (
            <>
              {!isAuthenticated ? (
                <Stack direction="column" onClick={() => navigate('/auth/login')} sx={{ cursor: 'pointer' }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#000' }}>
                    {translate('havent_login_yet')}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 400,
                      color: '#20202080',
                      lineHeight: 1,
                    }}
                  >
                    {translate('login_register_now')}
                  </Typography>
                </Stack>
              ) : (
                <Stack>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#000' }}>
                      {memberInfo?.username}
                    </Typography>
                    <Image
                      src={MEMBER_VIP_IMGS[memberInfo?.membershipLevel?.toLowerCase()]}
                      sx={{ width: isCollapse ? '100%' : 90, height: 25 }}
                    />
                  </Stack>

                  <Stack direction="row" alignItems="flex-end" spacing={0.5}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{memberBalance?.currencyCode}</Typography>
                    <Typography
                      sx={{
                        fontSize: 20,
                        fontWeight: 500,
                        color: '#000',
                        lineHeight: 1,
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        maxWidth: 150,
                      }}
                    >
                      {fCurrency(parseInt(memberBalance?.balance, 10))}
                    </Typography>
                  </Stack>
                </Stack>
              )}
            </>
          )}
        </Stack>

        {imageSrc && (
          <Image
            src={imageSrc}
            sx={{
              cursor: 'pointer',
              borderRadius: imageSrc === Asia889 ? 1 : 2,
              mt: 1,
              ...(imageSrc === Asia889
                ? {}
                : {
                    width: '100%',
                    height: 90,
                    objectFit: 'cover',
                  }),
            }}
          />
        )}

        <Stack
          direction="column"
          spacing={0.5}
          sx={{
            background: palette.navBarVertical.menu.bgColor,
            borderRadius: isCollapse ? '8px' : 2,
            p: isCollapse ? '8px 0' : '12px',
            width: '100%',
          }}
        >
          <MenuItem
            event={() => {
              dispatch(setActiveTab('home'));
              navigate('/');
            }}
            icon="home"
            label="home"
          />
          <MenuItem
            event={() => {
              dispatch(setActiveTab('favorite-game'));
              navigate('/favorite-game');
            }}
            icon="favorite"
            label="favorite-game"
          />
          <MenuItem
            event={() => {
              dispatch(setActiveTab('promotion'));
              navigate('/promotion');
            }}
            icon="promo"
            label="promotion"
          />
          <MenuItem
            event={() => {
              dispatch(
                openModal({
                  view: 'vip',
                })
              );
            }}
            icon="vip"
            label="vip"
          />
          {gameCategsAndCompanies.length > 0 &&
            gameCategsAndCompanies.map((item, index) => (
              <MenuItem
                key={index}
                event={() => {
                  dispatch(setActiveTab(item?.slug));
                  if (!isDesktop) onCloseSidebar();
                  if (window.location.pathname !== '/') navigate('/');
                }}
                icon={item.slug}
                label={item.name}
              />
            ))}
        </Stack>

        <Stack
          spacing={isCollapse ? 0 : 0.5}
          sx={{
            width: '100%',
            mt: -1,
            background: '#F6F7F9',
            borderRadius: 2,
            py: isCollapse ? 0 : 1,
            px: isCollapse ? 0 : 1,
          }}
        >
          <Box sx={{ mt: 0, width: '100%' }}>
            <LanguagePopover isCollapse={isCollapse} />
          </Box>
          {menuItems.map((item, index) => {
            const isActive = activeTab === item.key;
            return (
              <Box
                key={index}
                onClick={() => {
                  dispatch(setActiveTab(item.key));
                  navigate(item.path);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1.2,
                  borderRadius: isCollapse ? '8px' : 1.2,
                  cursor: 'pointer',
                  background: isActive ? theme.palette.gradients.theme : '#F6F7F9',
                  '& .nav-item-icon, & .nav-item-label': {
                    color: isActive ? '#fff' : '#9ca3af',
                  },
                  '&:hover': {
                    background: theme.palette.gradients.theme,
                    '& .nav-item-icon, & .nav-item-label': {
                      color: '#fff',
                    },
                  },
                  width: '100%',
                }}
              >
                <Iconify className="nav-item-icon" icon={item.icon} sx={{ fontSize: 22 }} />
                {!isCollapse && (
                  <Typography
                    className="nav-item-label"
                    sx={{
                      fontSize: 14,
                      fontWeight: isActive ? 700 : 500,
                    }}
                  >
                    {translate(item.label)}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
        {isAuthenticated && (
          <>
            <Button
              size="medium"
              variant="outlined"
              color="primary"
              onClick={() => {
                dispatch(
                  openModal({
                    view: 'logout',
                  })
                );
              }}
              sx={{
                minWidth: '100%',
                width: '100%',
                height: 36,
                px: isCollapse ? 0 : 2,
                borderRadius: 1,
                border: '2px solid',
                // background: palette.header.login_btn.bg,
                boxShadow: 'none',
                // color: '#FFCC00',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                ':hover': { background: 'transparent' },
              }}
            >
              {!isCollapse && translate('logout')}

              <Image
                src={require('../../../assets/side-nav/logout.png')}
                sx={{
                  width: 20,
                  ml: isCollapse ? 0 : 1,
                }}
              />
            </Button>
          </>
        )}
      </Stack>
    </Box>
  );
}

const MENU_ICONS = {
  home: require('../../../assets/side-nav/home.png'),
  promo: require('../../../assets/side-nav/promo.png'),
  favorite: require('../../../assets/side-nav/favorite.png'),
  sport: require('../../../assets/side-nav/sport.png'),
  live_casino: require('../../../assets/side-nav/live-casino.png'),
  slot: require('../../../assets/side-nav/slot.png'),
  mobile_slot: require('../../../assets/side-nav/mobile-slot.png'),
  crash: require('../../../assets/side-nav/crash.png'),
  eSports: require('../../../assets/side-nav/e-sports.png'),
  vip: require('../../../assets/side-nav/vip.png'),
};
